import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../../api';
import { useWS } from '../../communication/WSProvider';
import usePolling from '../../utils/usePolling';

// 60 s i synlig flik (A11, 2026-10-07): realtidskanalen ger direktbesked vid
// nytt meddelande, pollningen är reservvägen. Pausar i dold flik.
const INTERVALL_MS = 60000;

/** Antal olästa från servern, eller null om svaret inte var OK. */
async function hamtaRaknare(tenantSlug) {
  const res = await fetch(`${API_URL}/communication/inbox/unread-count`, {
    credentials: 'include',
    headers: {
      'X-Tenant-Slug': tenantSlug,
    },
  });
  if (!res.ok) return null;
  const js = await res.json();
  const n = Number(js?.unread);
  return Number.isFinite(n) ? n : 0;
}

function lasTenantSlug(activeTenantSlug) {
  let tenantSlug = activeTenantSlug;
  if (!tenantSlug) {
    try { tenantSlug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch {}
  }
  return tenantSlug;
}

/**
 * Räknaren för olästa direktmeddelanden vid Inkorg i menyn.
 *
 * Tre källor, som i Layout.js före utbrytningen (A11 steg 1, 2026-10-07):
 * 1. Prenumeration på inkorgsämnet via den delade WebSocket-anslutningen.
 * 2. Händelser: inbox.count.updated hämtar om från servern,
 *    direct.message.received räknar upp optimistiskt.
 * 3. Polling som reservväg (usePolling: paus i dold flik, hämtning direkt
 *    vid återkomst).
 *
 * Hämtningen går mot den lätta räknaren /communication/inbox/unread-count
 * (A11 steg 4): en aggregerad fråga i stället för 2,5 frågor och 2
 * filkontroller per kollega som /inbox/summary gör. Inkorg-sidan använder
 * fortfarande summary, som ger hela listan.
 *
 * @param {object|null|undefined} user Inloggad användare
 * @param {string} activeTenantSlug Aktiv tenant (faller tillbaka på localStorage)
 * @returns {number} Antal olästa
 */
export default function useInboxUnread(user, activeTenantSlug) {
  const [inboxUnread, setInboxUnread] = useState(0);
  // Use shared WS provider instead of a local connection
  const { lastEvent, subscribeInbox, unsubscribeInbox } = useWS() || {};

  // Subscribe to inbox events for current user via shared WS
  useEffect(() => {
    if (!user?.id || !subscribeInbox || !unsubscribeInbox) return;
    subscribeInbox(user.id);
    return () => { try { unsubscribeInbox(user.id); } catch {} };
  }, [user?.id, subscribeInbox, unsubscribeInbox]);

  // Handle inbox-related events from shared WS
  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === 'inbox.count.updated') {
      const tenantSlug = lasTenantSlug(activeTenantSlug);
      if (!tenantSlug) return;

      // Recompute from server for accuracy
      hamtaRaknare(tenantSlug)
        .then(total => { if (total !== null) setInboxUnread(total); })
        .catch(() => {});
    } else if (lastEvent.type === 'direct.message.received') {
      // Optimistically increment
      setInboxUnread(n => (n || 0) + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  // Poll inbox unread summary to show badge on left menu
  const hamta = useCallback(async () => {
    const tenantSlug = lasTenantSlug(activeTenantSlug);
    if (!tenantSlug) {
      setInboxUnread(0);
      return true;
    }
    try {
      const total = await hamtaRaknare(tenantSlug);
      if (total === null) return false;
      setInboxUnread(total);
      return true;
    } catch {
      return false;
    }
  }, [activeTenantSlug]);

  usePolling(hamta, { intervalMs: INTERVALL_MS, enabled: Boolean(user) });

  return inboxUnread;
}
